import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import axios from 'axios'
import { setAuthToken } from '../services/auth'
import API_URL from '../config'
import MediaActionButton from './common/MediaActionButton'
import UserMetadata from './common/UserMetadata'
import useMediaActions from '../hooks/useMediaActions'
import useRotation from '../hooks/useRotation'
import CollageSelectionModal from './CollageSelectionModal'
import { useUserMedia } from './Library/hooks/useUserMedia'
import { getServiceLinks, renderServiceLinksWithSeparators } from './Library/utils/serviceLinks'

function MixPage() {
  const [mix, setMix] = useState(null)
  const { id } = useParams()
  const [trackLoadingStatus, setTrackLoadingStatus] = useState({})
  const [showCollageModal, setShowCollageModal] = useState(false)
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 })
  const [selectedMediaForCollage, setSelectedMediaForCollage] = useState({
    type: 'mix',
    id: parseInt(id),
  })

  const {
    inQueue,
    inCollection,
    queueLoading,
    collectionLoading,
    handleAddToQueue,
    handleRemoveFromQueue,
    handleCollectionAction,
    fetchUserMedia,
    itemCollectionStatus,
    userCollection,
  } = useMediaActions('mix', id, mix?.songs || [])

  const { inRotation, isLoading: rotationLoading, toggleRotation } = useRotation('mix', id)

  const { userMedia } = useUserMedia()

  useEffect(() => {
    const fetchData = async () => {
      try {
        setAuthToken(localStorage.getItem('token'))
        const mixResponse = await axios.get(`${API_URL}mixes/${id}/`)
        setMix(mixResponse.data)

        const loadingStatus = {}
        mixResponse.data.songs.forEach((song) => {
          loadingStatus[song.id] = false
        })
        setTrackLoadingStatus(loadingStatus)
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }

    fetchData()
  }, [id])

  const handleitemCollectionAction = async (trackId, isRemoving) => {
    try {
      setTrackLoadingStatus((prev) => ({ ...prev, [trackId]: true }))
      if (isRemoving) {
        await axios.delete(`${API_URL}user-media/song/${trackId}/`)
      } else {
        await axios.post(`${API_URL}add-to-collection/`, {
          media_type: 'song',
          id: trackId,
        })
      }
      await fetchUserMedia()
    } catch (error) {
      console.error(`Error ${isRemoving ? 'removing' : 'adding'} track to collection:`, error)
    } finally {
      setTrackLoadingStatus((prev) => ({ ...prev, [trackId]: false }))
    }
  }

  const renderServiceLinks = (item, type) => {
    if (!item.service_links || Object.keys(item.service_links).length === 0) {
      return <span className='metadata-text secondary'>No links available</span>
    }

    return (
      <div className='service-links'>
        {renderServiceLinksWithSeparators(getServiceLinks(item, type))}
      </div>
    )
  }

  // Function to render service links for multiple songs
  const renderCombinedServiceLinks = (songs, type) => {
    // Get all service links from all songs
    const allLinks = songs.reduce((links, song) => {
      if (!song.service_links) return links

      // Convert service_links object to array of links
      const songLinks = getServiceLinks(song, type)
      return [...links, ...songLinks]
    }, [])

    if (allLinks.length === 0) {
      return <span className='metadata-text secondary'>No links available</span>
    }

    return <div className='service-links'>{renderServiceLinksWithSeparators(allLinks)}</div>
  }

  const handleAddToCollage = (event, mediaType = 'mix', mediaId = id) => {
    const button = event.currentTarget
    const rect = button.getBoundingClientRect()

    // Toggle the modal
    if (showCollageModal) {
      setShowCollageModal(false)
    } else {
      setModalPosition({
        top: rect.bottom,
        left: rect.left,
      })
      setShowCollageModal(true)
    }
    // Store the selected media info
    setSelectedMediaForCollage({ type: mediaType, id: parseInt(mediaId) })
  }

  // Return true if the songs should be consolidated (same title/position)
  const shouldConsolidate = (song1, song2) => {
    return song1.title === song2.title && song1.position === song2.position
  }

  // Consolidate songs with the same name and position
  const getConsolidatedSongs = () => {
    if (!mix || !mix.songs) return []

    const consolidated = []
    const processed = new Set()

    mix.songs.forEach((song, index) => {
      // Skip if already processed
      if (processed.has(song.id)) return

      // Find all songs that should be consolidated with this one
      const matches = mix.songs.filter(
        (s) => (s.id === song.id || shouldConsolidate(s, song)) && !processed.has(s.id)
      )

      // Mark all these songs as processed
      matches.forEach((s) => processed.add(s.id))

      if (matches.length > 1) {
        // Create a consolidated entry
        consolidated.push({
          ...song,
          isConsolidated: true,
          relatedSongs: matches,
          relatedIds: matches.map((s) => s.id),
        })
      } else {
        // Just add the single song
        consolidated.push(song)
      }
    })

    return consolidated
  }

  if (!mix) return <div>Loading...</div>

  const consolidatedSongs = getConsolidatedSongs()

  return (
    <div className='media-list'>
      <div className='media-header'>
        <h1
          className={`metadata-text primary rotation-title ${inRotation ? 'in-rotation' : ''}`}
          onClick={toggleRotation}
        >
          {mix.title}
        </h1>
        <div className='media-actions'>
          <MediaActionButton
            onClick={inQueue ? handleRemoveFromQueue : handleAddToQueue}
            isActive={inQueue}
            isLoading={queueLoading}
            actionType='Queue'
          />
          <MediaActionButton
            onClick={() => handleCollectionAction(inCollection)}
            isActive={inCollection}
            isLoading={collectionLoading}
            actionType='Collection'
          />
          <MediaActionButton
            onClick={handleAddToCollage}
            isActive={false}
            isLoading={false}
            actionType='Collage'
          />
        </div>
      </div>

      <div className='metadata'>
        <div className='info-line'>
          <span className='metadata-text secondary'>Songs: {consolidatedSongs.length}</span>
        </div>
        <div className='service-links center'>{renderServiceLinks(mix, 'mix')}</div>
      </div>

      <div className='mt-5'>
        <ul className='left-justify'>
          {consolidatedSongs.map((song) => (
            <li key={song.id}>
              <div className='metadata'>
                <Link to={`/song/${song.id}`} className='metadata-text primary interactive'>
                  {song.title}
                </Link>
                {song.primary_artists && song.primary_artists.length > 0 && (
                  <>
                    <span className='metadata-text secondary'> by </span>
                    {song.primary_artists
                      .map((artist) => (
                        <Link
                          key={artist.id}
                          to={`/artist/${artist.id}`}
                          className='metadata-text primary interactive'
                        >
                          {artist.name}
                        </Link>
                      ))
                      .reduce((prev, curr) => [prev, ', ', curr])}
                  </>
                )}
                {song.isConsolidated
                  ? // For consolidated songs, show combined service links
                    renderCombinedServiceLinks(song.relatedSongs, 'song')
                  : // For regular songs, show normal service links
                    renderServiceLinks(song, 'song')}
              </div>
              <div className='media-actions'>
                <MediaActionButton
                  onClick={() => handleitemCollectionAction(song.id, itemCollectionStatus[song.id])}
                  isActive={itemCollectionStatus[song.id]}
                  isLoading={trackLoadingStatus[song.id]}
                  actionType='Collection'
                />
                <MediaActionButton
                  onClick={(event) => handleAddToCollage(event, 'song', song.id)}
                  isActive={false}
                  isLoading={false}
                  actionType='Collage'
                />
              </div>
            </li>
          ))}
        </ul>
      </div>

      {showCollageModal && (
        <CollageSelectionModal
          position={modalPosition}
          onClose={() => setShowCollageModal(false)}
          selectedMediaForCollage={selectedMediaForCollage}
          fetchUserMedia={fetchUserMedia}
          collages={userMedia?.collection?.collages || []}
        />
      )}

      {/* User metadata: notes and recommendations */}
      <UserMetadata mediaType='mix' mediaId={id} />
    </div>
  )
}

export default MixPage
